import { z } from 'zod'
import { publicIdSchema, utcTimestampSchema } from './primitives'

// ---------------------------------------------------------------------------
// Registration form answers — one `registration_form` submission per account
// per competition (Phase 3's form renderer, applied to the individual who is
// registering, mirroring `team_form_answers`).
// ---------------------------------------------------------------------------

export const registrationFormAnswersSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  userId: publicIdSchema,
  formId: publicIdSchema,
  formVersionId: publicIdSchema,
  answers: z.record(z.string(), z.unknown()),
  /** Optimistic concurrency counter — starts at 1, increments on every successful save. */
  version: z.number().int().min(1),
  createdAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type RegistrationFormAnswers = z.infer<typeof registrationFormAnswersSchema>

export const updateRegistrationFormAnswersRequestSchema = z.strictObject({
  answers: z.record(z.string(), z.unknown()),
  /**
   * The caller's last-known `version`. Omitted entirely is treated as "I
   * don't know/don't care" (always succeeds) — required only by callers
   * that want the 409 conflict guard (the autosave/manual-save UI always
   * sends it once it has read a row at least once).
   */
  version: z.number().int().min(1).optional(),
})
export type UpdateRegistrationFormAnswersRequest = z.infer<
  typeof updateRegistrationFormAnswersRequestSchema
>

// ---------------------------------------------------------------------------
// Registration Data (SDD v1.1 §4.3.3) — competition-wide, admin-facing
// summary of every team's registration-form completion. One row per team
// (leader), left-joined against that leader's answer to the competition's
// currently-active `registration_form` — the exact same existence
// semantics `admin-operations.ts`'s Operations Dashboard "registration
// completion" KPI already uses (BR-156), never a second completion rule.
//
// Deliberately does NOT embed `answers_json` — admin-defined custom form
// content, potentially containing arbitrary user-submitted text (SDD
// §4.3.3/CLAUDE.md data-minimization convention already used by
// `EnrollmentProofReviewDto` etc.). A row's raw answers are only available
// through the separate per-team detail fetch below.
// ---------------------------------------------------------------------------

export const registrationDataRowDtoSchema = z.object({
  teamId: publicIdSchema,
  teamName: z.string(),
  leaderUserId: publicIdSchema,
  leaderDisplayName: z.string(),
  leaderEmail: z.string(),
  formId: publicIdSchema.nullable(),
  formVersionId: publicIdSchema.nullable(),
  formVersionNumber: z.number().int().min(1).nullable(),
  completed: z.boolean(),
  submittedAt: utcTimestampSchema.nullable(),
  updatedAt: utcTimestampSchema.nullable(),
})
export type RegistrationDataRowDto = z.infer<typeof registrationDataRowDtoSchema>

export const registrationDataQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  offset: z.coerce.number().int().min(0).default(0),
  teamId: publicIdSchema.optional(),
  completed: z.enum(['true', 'false']).optional(),
  search: z.string().trim().max(200).optional(),
})
export type RegistrationDataQuery = z.infer<typeof registrationDataQuerySchema>

/** Detail fetch (progressive disclosure) — one team's raw registration answers. */
export const registrationDataAnswersDtoSchema = z.object({
  teamId: publicIdSchema,
  formId: publicIdSchema,
  formVersionId: publicIdSchema,
  formVersionNumber: z.number().int().min(1).nullable(),
  answers: z.record(z.string(), z.unknown()),
  submittedAt: utcTimestampSchema,
  updatedAt: utcTimestampSchema,
})
export type RegistrationDataAnswersDto = z.infer<typeof registrationDataAnswersDtoSchema>

// ---------------------------------------------------------------------------
// Registration analytics events (mirrors `help.ts`'s `helpUiEventSchema`
// family — one generic event log, event_name selects which properties are
// meaningful). Authenticated: every event carries the caller's own
// competitionId, inferred server-side from the route/session, never
// client-supplied identity.
// ---------------------------------------------------------------------------

export const REGISTRATION_ANALYTICS_EVENT_NAMES = [
  'registration_form_opened',
  'registration_draft_saved',
  'registration_submit_clicked',
  'registration_submit_succeeded',
  'registration_submit_failed',
  'eligibility_upload_started',
  'eligibility_upload_succeeded',
] as const
export type RegistrationAnalyticsEventName = (typeof REGISTRATION_ANALYTICS_EVENT_NAMES)[number]

export const registrationAnalyticsEventSchema = z.object({
  eventName: z.enum(REGISTRATION_ANALYTICS_EVENT_NAMES),
  /** `registration_submit_failed` only; ignored for every other event. */
  errorCode: z.string().max(64).optional(),
})
export type RegistrationAnalyticsEvent = z.infer<typeof registrationAnalyticsEventSchema>

export const registrationAnalyticsEventsBatchRequestSchema = z.object({
  events: z.array(registrationAnalyticsEventSchema).min(1).max(20),
})
export type RegistrationAnalyticsEventsBatchRequest = z.infer<
  typeof registrationAnalyticsEventsBatchRequestSchema
>

export const platformRegistrationAnalyticsEventDtoSchema = z.object({
  id: publicIdSchema,
  competitionId: publicIdSchema,
  userId: publicIdSchema,
  eventName: z.enum(REGISTRATION_ANALYTICS_EVENT_NAMES),
  errorCode: z.string().nullable(),
  createdAt: utcTimestampSchema,
})
export type PlatformRegistrationAnalyticsEventDto = z.infer<
  typeof platformRegistrationAnalyticsEventDtoSchema
>

/**
 * AC-367: a student whose saved registration no longer satisfies the current
 * published form. Deliberately carries no email and no answers (data
 * minimization): recipients are resolved server-side when reminders are sent.
 */
export const registrationNeedsUpdateRowDtoSchema = z.strictObject({
  userId: publicIdSchema,
  displayName: z.string(),
  teamName: z.string().nullable(),
  missingFields: z.array(z.strictObject({ key: z.string(), label: z.string() })),
  updatedAt: utcTimestampSchema,
})
export type RegistrationNeedsUpdateRowDto = z.infer<typeof registrationNeedsUpdateRowDtoSchema>

export const registrationNeedsUpdateReminderResultDtoSchema = z.strictObject({
  recipientCount: z.number().int().min(0),
  userIds: z.array(publicIdSchema),
  failedUserIds: z.array(publicIdSchema),
})
export type RegistrationNeedsUpdateReminderResultDto = z.infer<
  typeof registrationNeedsUpdateReminderResultDtoSchema
>

/** AC-369: the email an organizer is about to send, shown before sending. Carries no address; sampled from one student who needs an update. */
export const registrationNeedsUpdateEmailPreviewDtoSchema = z.strictObject({
  subject: z.string(),
  text: z.string(),
  sampleDisplayName: z.string(),
  recipientCount: z.number().int().min(1),
})
export type RegistrationNeedsUpdateEmailPreviewDto = z.infer<
  typeof registrationNeedsUpdateEmailPreviewDtoSchema
>
